/**
 * Client-side conversation cache (localStorage).
 *
 * Why this exists:
 *   - AIAccountant turns (file uploads, financial reports) bypass axon-secretary
 *     entirely — they go straight to /api/v1/aiaccountant/agent. Secretary's
 *     conversation store never sees those messages.
 *   - Without a backend "append-only" endpoint, switching away from an
 *     AIAccountant conversation and back wipes the bubbles.
 *   - This module persists the user-message + agent-reply pairs locally so
 *     they survive reloads + conversation switches.
 *
 * Hybrid display strategy:
 *   - SessionList merges server conversations with locally-cached ones.
 *   - Conversation pane merges server messages with locally-cached ones,
 *     dedupes by id, sorts by created_at.
 *
 * Storage layout:
 *   axondesktop.localConvs         → JSON array of Conversation summaries
 *   axondesktop.msgs.{convId}      → JSON array of Message for that conv
 *
 * Limits: localStorage is ~5MB per origin. We auto-truncate any conversation
 * over 500 messages (very generous for our workflow).
 */

import type { Conversation, Message } from '@/types/chat';

const KEY_CONVS = 'axondesktop.localConvs';
const MSG_KEY = (id: string) => `axondesktop.msgs.${id}`;
const MAX_MSGS_PER_CONV = 500;

// ── Messages ────────────────────────────────────────────────────────────

export function getCachedMessages(conversationId: string): Message[] {
  if (!conversationId) return [];
  try {
    const raw = localStorage.getItem(MSG_KEY(conversationId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as Message[]) : [];
  } catch {
    return [];
  }
}

export function setCachedMessages(conversationId: string, messages: Message[]) {
  if (!conversationId) return;
  try {
    const trimmed = messages.slice(-MAX_MSGS_PER_CONV);
    localStorage.setItem(MSG_KEY(conversationId), JSON.stringify(trimmed));
  } catch {
    /* ignore quota errors */
  }
}

export function appendCachedMessage(conversationId: string, message: Message) {
  const cur = getCachedMessages(conversationId);
  // Dedupe by id if we somehow append the same message twice.
  const without = cur.filter((m) => m.id !== message.id);
  setCachedMessages(conversationId, [...without, message]);
}

/**
 * Merge server + local messages. Local wins for any duplicate id.
 * Sort by created_at ascending so the latest exchange is at the bottom.
 */
export function mergeMessages(server: Message[], local: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const m of server) byId.set(m.id, m);
  for (const m of local) byId.set(m.id, m); // local overrides
  return [...byId.values()].sort((a, b) =>
    (a.created_at ?? '').localeCompare(b.created_at ?? ''),
  );
}

// ── Conversations (sidebar) ────────────────────────────────────────────

export function listLocalConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(KEY_CONVS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as Conversation[]) : [];
  } catch {
    return [];
  }
}

export function saveLocalConversation(conv: Conversation) {
  const list = listLocalConversations();
  const without = list.filter((c) => c.id !== conv.id);
  const updated = [conv, ...without];
  try {
    localStorage.setItem(KEY_CONVS, JSON.stringify(updated.slice(0, 200)));
  } catch {
    /* ignore */
  }
}

export function deleteLocalConversation(conversationId: string) {
  try {
    localStorage.setItem(
      KEY_CONVS,
      JSON.stringify(listLocalConversations().filter((c) => c.id !== conversationId)),
    );
    localStorage.removeItem(MSG_KEY(conversationId));
  } catch {
    /* ignore */
  }
}

/** Merge server conversations with local-only ones, dedupe by id. */
export function mergeConversations(
  server: Conversation[],
  local: Conversation[],
): Conversation[] {
  const byId = new Map<string, Conversation>();
  for (const c of local) byId.set(c.id, c);
  for (const c of server) {
    const existing = byId.get(c.id);
    // Server wins on duplicate — EXCEPT when the server still has the default
    // "New Conversation" placeholder and the local cache has a real title set
    // after the first import/turn (e.g. "Rekha Amit Kumar — Kotak Bank").
    if (existing && c.title === 'New Conversation' && existing.title !== 'New Conversation') {
      byId.set(c.id, {
        ...c,
        title: existing.title,
        last_message_preview: existing.last_message_preview || c.last_message_preview,
        updated_at: existing.updated_at || c.updated_at,
      });
    } else {
      byId.set(c.id, c);
    }
  }
  return [...byId.values()].sort((a, b) =>
    (b.updated_at ?? b.created_at ?? '').localeCompare(
      a.updated_at ?? a.created_at ?? '',
    ),
  );
}

/**
 * Derive a friendly title from the first user message of a conversation.
 * Used to upgrade "New Conversation" placeholders once the user actually
 * sends a message.
 */
export function titleFromMessage(content: string): string {
  const trimmed = content.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= 40) return trimmed || 'New Conversation';
  return trimmed.slice(0, 40) + '…';
}

/**
 * After a successful import/report run, persist the exchange to cache and
 * upgrade the conversation summary so the sidebar is in sync.
 */
export function persistTurn(
  conversationId: string,
  userMessage: Message,
  assistantMessage: Message,
  conversationTitle?: string,
) {
  appendCachedMessage(conversationId, userMessage);
  appendCachedMessage(conversationId, assistantMessage);

  const now = new Date().toISOString();
  saveLocalConversation({
    id: conversationId,
    title: conversationTitle || titleFromMessage(userMessage.content),
    created_at: now,
    updated_at: now,
    last_message_preview: assistantMessage.content.slice(0, 80),
    message_count: getCachedMessages(conversationId).length,
  });
  // Fire global event so SessionList re-fetches
  window.dispatchEvent(new CustomEvent('axondesktop.conversations.updated'));
}
